// Gestionnaire de stockage offline
export class OfflineStorageManager {
    constructor() {
        this.databases = ['products', 'substances', 'profiles', 'localData'];
        this.pouchDBInstances = {};
    }

    // Obtenir ou créer une instance PouchDB
    async getPouchDBInstance(dbName) {
        if (!this.pouchDBInstances[dbName]) {
            try {
                const PouchDB = (await import('pouchdb')).default;
                this.pouchDBInstances[dbName] = new PouchDB(dbName);
            } catch (error) {
                return null;
            }
        }
        return this.pouchDBInstances[dbName];
    }

    // Fermer toutes les instances PouchDB
    async closeAllDatabases() {
        for (const [dbName, db] of Object.entries(this.pouchDBInstances)) {
            try {
                if (db && typeof db.close === 'function') {
                    await db.close();
                }
            } catch (error) {

            }
        }
        this.pouchDBInstances = {};
    }

    // Calculer la taille totale des données stockées
    async calculateStorageSize() {
        try {
            let totalSize = 0;

            // Taille du cache du service worker
            if ('caches' in window) {
                const cacheNames = await caches.keys();
                for (const cacheName of cacheNames) {
                    const cache = await caches.open(cacheName);
                    const requests = await cache.keys();
                    for (const request of requests) {
                        const response = await cache.match(request);
                        if (response) {
                            const clone = response.clone();
                            const buffer = await clone.arrayBuffer();
                            totalSize += buffer.byteLength;
                        }
                    }
                }
            }

            // Taille des bases de données PouchDB (estimation)
            if (typeof window !== 'undefined' && window.indexedDB) {
                // Estimation basée sur le nombre d'éléments stockés
                const estimation = await this.estimatePouchDBSize();
                totalSize += estimation;
            }

            return this.formatBytes(totalSize);
        } catch (error) {
            return 'Inconnu';
        }
    }

    // Estimer la taille des données PouchDB
    async estimatePouchDBSize() {
        try {
            let estimatedSize = 0;

            for (const dbName of this.databases) {
                try {
                    const db = await this.getPouchDBInstance(dbName);
                    if (db) {
                        const info = await db.info();
                        // Estimation : environ 2KB par document en moyenne
                        estimatedSize += info.doc_count * 2048;
                    }
                } catch (dbError) {
                }
            }

            return estimatedSize;
        } catch (error) {
            return 0;
        }
    }

    // Vérifier l'espace disponible
    async checkAvailableStorage() {
        try {
            if ('storage' in navigator && 'estimate' in navigator.storage) {
                const estimate = await navigator.storage.estimate();
                return {
                    quota: estimate.quota,
                    usage: estimate.usage,
                    available: estimate.quota - estimate.usage,
                    usageDetails: estimate.usageDetails
                };
            }
            return null;
        } catch (error) {
            return null;
        }
    }

    // Nettoyer le cache
    async clearCache() {
        try {
            if ('caches' in window) {
                const cacheNames = await caches.keys();
                await Promise.all(
                    cacheNames.map(cacheName => caches.delete(cacheName))
                );
            }
            return true;
        } catch (error) {

            return false;
        }
    }

    // Nettoyer les données PouchDB
    async clearPouchDBData() {
        try {
            // Fermer toutes les instances ouvertes
            await this.closeAllDatabases();

            // Maintenant détruire les bases
            const PouchDB = (await import('pouchdb')).default;

            for (const dbName of this.databases) {
                try {
                    const db = new PouchDB(dbName);
                    await db.destroy();
                } catch (dbError) {
                }
            }

            // Nettoyer les instances
            this.pouchDBInstances = {};
            return true;
        } catch (error) {
            return false;
        }
    }

    // Nettoyer toutes les données offline
    async clearAllOfflineData() {
        try {
            const cacheCleared = await this.clearCache();
            const dbCleared = await this.clearPouchDBData();

            // Nettoyer le localStorage
            if (typeof window !== 'undefined' && window.localStorage) {
                const keysToRemove = [];
                for (let i = 0; i < localStorage.length; i++) {
                    const key = localStorage.key(i);
                    if (key && (key.includes('pouchdb') || key.includes('offline') || key.includes('cache'))) {
                        keysToRemove.push(key);
                    }
                }
                keysToRemove.forEach(key => localStorage.removeItem(key));
            }

            return cacheCleared && dbCleared;
        } catch (error) {
            return false;
        }
    }

    // Vérifier le statut offline
    async getOfflineStatus() {
        try {
            const status = {
                isOfflineCapable: true,
                databases: {},
                cacheStatus: 'unknown',
                totalSize: await this.calculateStorageSize(),
                lastSync: localStorage.getItem('last_offline_sync') || null
            };

            // Vérifier chaque base de données
            for (const dbName of this.databases) {
                try {
                    const db = await this.getPouchDBInstance(dbName);
                    if (db) {
                        const info = await db.info();
                        status.databases[dbName] = {
                            docCount: info.doc_count || 0,
                            updateSeq: info.update_seq || 0,
                            size: `${Math.round((info.doc_count || 0) * 2 / 1024)} KB`
                        };
                    } else {
                        status.databases[dbName] = { docCount: 0, error: 'Base non disponible' };
                    }
                } catch (dbError) {
                    status.databases[dbName] = {
                        docCount: 0,
                        error: dbError.message
                    };
                }
            }

            // Vérifier le cache
            if ('caches' in window) {
                try {
                    const cacheNames = await caches.keys();
                    status.cacheStatus = cacheNames.length > 0 ? 'available' : 'empty';
                } catch (cacheError) {
                    status.cacheStatus = 'error';
                }
            }

            return status;
        } catch (error) {
            return {
                isOfflineCapable: false,
                error: error.message,
                databases: {},
                totalSize: '0 Bytes'
            };
        }
    }

    // Formater les octets en unités lisibles
    formatBytes(bytes, decimals = 2) {
        if (bytes === 0) return '0 Bytes';

        const k = 1024;
        const dm = decimals < 0 ? 0 : decimals;
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

        const i = Math.floor(Math.log(bytes) / Math.log(k));

        return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
    }

    // Synchronisation périodique en arrière-plan
    async scheduleBackgroundSync() {
        if ('serviceWorker' in navigator && 'sync' in window.ServiceWorkerRegistration.prototype) {
            try {
                const registration = await navigator.serviceWorker.ready;
                await registration.sync.register('background-sync-data');
                return true;
            } catch (error) {
                return false;
            }
        }
        return false;
    }

    // Vérifier la connectivité réseau
    async testConnectivity() {
        if (!navigator.onLine) {
            return false;
        }

        try {
            const response = await fetch('/manifest.json', {
                method: 'HEAD',
                cache: 'no-cache',
                timeout: 5000
            });
            return response.ok;
        } catch {
            return false;
        }
    }
}

export default new OfflineStorageManager();
